import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { Link } from 'react-router';
import {
  ReportingInset,
  ReportingPanel,
  ReportingSectionHeader,
  ReportingStatusPill,
} from './ReportingChrome';
import { ReportingMetricCard } from './ReportingMetricCard';
import type {
  ReportingAutomatedReviewCheckMetric,
  ReportingAutomatedReviewMetrics,
  ReportingAutomatedReviewRunLink,
} from '../types';
import {
  getAdminDappPath,
  getDappReleasePath,
} from '../../../utils/dappRoutes';

interface AutomatedReviewSectionProps {
  metrics: ReportingAutomatedReviewMetrics;
}

export function AutomatedReviewSection({
  metrics,
}: AutomatedReviewSectionProps) {
  const { summary, reruns, overrides } = metrics;
  const nonPassingRuns =
    summary.failedRuns + summary.errorRuns + summary.staleRuns;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <ReportingMetricCard
          label="Policy pass rate"
          value={formatPercent(summary.passRate)}
          hint={`${summary.policyEvaluatedRuns.toLocaleString()} pass/fail runs`}
          tone={
            summary.passRate !== null && summary.passRate < 90
              ? 'amber'
              : 'teal'
          }
          emphasis="highlight"
        />
        <ReportingMetricCard
          label="Needs attention"
          value={nonPassingRuns.toLocaleString()}
          hint={`${summary.failedRuns.toLocaleString()} failed · ${summary.errorRuns.toLocaleString()} errors · ${summary.staleRuns.toLocaleString()} stale`}
          tone={nonPassingRuns > 0 ? 'amber' : 'teal'}
        />
        <ReportingMetricCard
          label="P95 runtime"
          value={formatDuration(summary.p95DurationMs)}
          hint={`P50 ${formatDuration(summary.p50DurationMs)}`}
        />
        <ReportingMetricCard
          label="Same-input retries"
          value={reruns.sameInputExtraAttempts.toLocaleString()}
          hint={`${reruns.ticketsWithSameInputRetries.toLocaleString()} tickets retried`}
          tone={reruns.sameInputExtraAttempts > 0 ? 'amber' : 'teal'}
        />
        <ReportingMetricCard
          label="Active overrides"
          value={overrides.summary.activeOverrides.toLocaleString()}
          hint={`${overrides.summary.allTimeOverrides.toLocaleString()} all-time overrides`}
          tone={overrides.summary.activeOverrides > 0 ? 'amber' : 'teal'}
        />
      </div>

      {metrics.callouts.length > 0 && (
        <div className="grid gap-2 lg:grid-cols-2">
          {metrics.callouts.map((callout) => (
            <ReportingInset
              key={`${callout.title}-${callout.detail}`}
              className="p-3"
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={`mt-1.5 size-1.5 shrink-0 rounded-full ${calloutToneClass(
                    callout.tone,
                  )}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-neutral-200">
                    {callout.title}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-neutral-500">
                    {callout.detail}
                  </p>
                </div>
              </div>
            </ReportingInset>
          ))}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <ReportingPanel className="p-5">
          <ReportingSectionHeader
            title="Policy Trend"
            subtitle="Daily run outcomes and pass rate, with stale/error kept separate from policy pass/fail."
          />
          <DailyTrend metrics={metrics} />
        </ReportingPanel>

        <ReportingPanel className="p-5">
          <ReportingSectionHeader
            title="Policy Versions"
            subtitle="Compare policy/runtime versions before reading the aggregate."
          />
          <PolicyTable metrics={metrics} />
        </ReportingPanel>
      </div>

      <ReportingPanel className="p-5">
        <ReportingSectionHeader
          title="Check Health"
          subtitle="Highest-signal checks by failure, warning, skipped, error, or mixed same-input outcomes."
        />
        <CheckHealthTable checks={metrics.checks} />
      </ReportingPanel>

      <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <ReportingPanel className="p-5">
          <ReportingSectionHeader
            title="Category Heatmap"
            subtitle="Category and severity rollup for fast policy-area scanning."
          />
          <CategoryTable metrics={metrics} />
        </ReportingPanel>

        <ReportingPanel className="p-5">
          <ReportingSectionHeader
            title="Run Worklist"
            subtitle="Latest non-passing or stale runs in the selected window."
          />
          <RunWorklist runs={metrics.worklist} />
        </ReportingPanel>
      </div>

      <ReportingPanel className="p-5">
        <ReportingSectionHeader
          title="Override Risk"
          subtitle="Human overrides of automated verdicts, without reviewer or publisher PII."
        />
        <OverridePanel metrics={metrics} />
      </ReportingPanel>
    </div>
  );
}

function DailyTrend({ metrics }: { metrics: ReportingAutomatedReviewMetrics }) {
  if (metrics.daily.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No automated-review runs are present in this date range.
      </p>
    );
  }

  const maxRuns = Math.max(...metrics.daily.map((point) => point.runs), 1);

  return (
    <div className="mt-4 space-y-2">
      {metrics.daily.map((point) => {
        const passedWidth = (point.passedRuns / maxRuns) * 100;
        const failedWidth = (point.failedRuns / maxRuns) * 100;
        const staleWidth = (point.staleRuns / maxRuns) * 100;
        const errorWidth = (point.errorRuns / maxRuns) * 100;

        return (
          <div
            key={point.date}
            className="grid gap-3 sm:grid-cols-[5.5rem_1fr_4.5rem]"
          >
            <div className="text-xs tabular-nums text-neutral-500">
              {formatDate(point.date)}
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-neutral-800/60">
              <div className="flex h-full">
                <div
                  className="bg-teal-400/70"
                  style={{ width: `${passedWidth}%` }}
                />
                <div
                  className="bg-rose-400/70"
                  style={{ width: `${failedWidth}%` }}
                />
                <div
                  className="bg-amber-400/70"
                  style={{ width: `${staleWidth}%` }}
                />
                <div
                  className="bg-red-500/70"
                  style={{ width: `${errorWidth}%` }}
                />
              </div>
            </div>
            <div className="text-right text-xs tabular-nums text-neutral-400">
              {formatPercent(point.passRate)}
            </div>
          </div>
        );
      })}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-500">
        <LegendDot className="bg-teal-400/70" label="Passed" />
        <LegendDot className="bg-rose-400/70" label="Failed" />
        <LegendDot className="bg-amber-400/70" label="Stale" />
        <LegendDot className="bg-red-500/70" label="Error" />
      </div>
    </div>
  );
}

function PolicyTable({
  metrics,
}: {
  metrics: ReportingAutomatedReviewMetrics;
}) {
  if (metrics.policies.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No policy versions are present in this date range.
      </p>
    );
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full text-left text-xs">
        <thead className="border-b border-neutral-800/70 text-[11px] uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="px-3 py-2 font-medium">Policy</th>
            <th className="px-3 py-2 text-right font-medium">Runs</th>
            <th className="px-3 py-2 text-right font-medium">Pass</th>
            <th className="px-3 py-2 text-right font-medium">Stale</th>
            <th className="px-3 py-2 text-right font-medium">P95</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800/50">
          {metrics.policies.map((policy) => (
            <tr key={policy.policyVersion}>
              <td className="max-w-[18rem] p-3">
                <div
                  className="truncate font-mono text-[11px] text-neutral-300"
                  title={policy.policyVersion}
                >
                  {policy.policyVersion}
                </div>
                <div className="mt-1 text-[11px] text-neutral-600">
                  Latest {formatNullableDate(policy.latestRunAt)}
                </div>
              </td>
              <td className="p-3 text-right tabular-nums text-neutral-300">
                {policy.runs.toLocaleString()}
              </td>
              <td className="p-3 text-right tabular-nums text-neutral-300">
                {formatPercent(policy.passRate)}
              </td>
              <td className="p-3 text-right tabular-nums text-amber-300">
                {policy.staleRuns.toLocaleString()}
              </td>
              <td className="p-3 text-right tabular-nums text-neutral-400">
                {formatDuration(policy.p95DurationMs)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CheckHealthTable({
  checks,
}: {
  checks: ReportingAutomatedReviewCheckMetric[];
}) {
  if (checks.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No check results are present in this date range.
      </p>
    );
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full text-left text-xs">
        <thead className="border-b border-neutral-800/70 text-[11px] uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="px-3 py-2 font-medium">Check</th>
            <th className="px-3 py-2 text-right font-medium">Signals</th>
            <th className="px-3 py-2 text-right font-medium">Pass</th>
            <th className="px-3 py-2 text-right font-medium">P95</th>
            <th className="px-3 py-2 text-right font-medium">Drift</th>
            <th className="px-3 py-2 font-medium">Latest runs</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800/50">
          {checks.map((check) => {
            const signals =
              check.failed +
              check.warnings +
              check.skipped +
              check.errors +
              check.incomplete;

            return (
              <tr key={check.checkKey}>
                <td className="min-w-[18rem] p-3 align-top">
                  <div className="font-medium text-neutral-200">
                    {check.displayName}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <ReportingStatusPill label={check.category} />
                    <ReportingStatusPill
                      label={check.severity}
                      tone={severityTone(check.severity)}
                    />
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-neutral-600">
                    {check.checkKey}
                  </div>
                </td>
                <td className="p-3 text-right align-top tabular-nums text-neutral-300">
                  {signals.toLocaleString()}
                  <div className="mt-1 text-[11px] text-neutral-600">
                    {check.executions.toLocaleString()} runs
                  </div>
                </td>
                <td className="p-3 text-right align-top tabular-nums text-neutral-300">
                  {formatPercent(check.passRate)}
                  <div className="mt-1 text-[11px] text-neutral-600">
                    {formatPercent(check.signalRate)} signal
                  </div>
                </td>
                <td className="p-3 text-right align-top tabular-nums text-neutral-400">
                  {formatDuration(check.p95DurationMs)}
                </td>
                <td className="p-3 text-right align-top tabular-nums text-neutral-400">
                  {check.mixedOutcomeInputs.toLocaleString()}
                </td>
                <td className="min-w-[14rem] p-3 align-top">
                  <RunLinkList runs={check.latestAffectedRuns} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CategoryTable({
  metrics,
}: {
  metrics: ReportingAutomatedReviewMetrics;
}) {
  if (metrics.categories.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No category-level check results are present in this date range.
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {metrics.categories.slice(0, 10).map((category) => {
        const signals =
          category.failed +
          category.warnings +
          category.skipped +
          category.errors;

        return (
          <ReportingInset
            key={`${category.category}-${category.severity}`}
            className="p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-neutral-200">
                    {category.category}
                  </span>
                  <ReportingStatusPill
                    label={category.severity}
                    tone={severityTone(category.severity)}
                  />
                </div>
                <div className="mt-1 text-xs text-neutral-500">
                  {signals.toLocaleString()} signals ·{' '}
                  {category.executions.toLocaleString()} executions
                </div>
              </div>
              <div className="text-right text-sm tabular-nums text-neutral-300">
                {formatPercent(category.passRate)}
              </div>
            </div>
          </ReportingInset>
        );
      })}
    </div>
  );
}

function RunWorklist({ runs }: { runs: ReportingAutomatedReviewRunLink[] }) {
  if (runs.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No non-passing automated-review runs are current in this range.
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {runs.slice(0, 8).map((run) => (
        <RunRow key={run.runId} run={run} />
      ))}
    </div>
  );
}

function OverridePanel({
  metrics,
}: {
  metrics: ReportingAutomatedReviewMetrics;
}) {
  const { summary } = metrics.overrides;

  if (summary.allTimeOverrides === 0) {
    return (
      <ReportingInset className="mt-4 p-4">
        <div className="text-sm font-medium text-neutral-200">
          No overrides recorded
        </div>
        <p className="mt-1 text-xs leading-relaxed text-neutral-500">
          AutomatedReviewCheckOverride has no rows, so override concentration is
          not a meaningful risk signal yet.
        </p>
      </ReportingInset>
    );
  }

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[18rem_1fr]">
      <ReportingInset className="p-3.5">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500">
          Overrides in range
        </div>
        <div className="mt-1.5 text-2xl font-semibold tabular-nums text-white">
          {summary.totalOverrides.toLocaleString()}
        </div>
        <div className="mt-2 text-xs text-neutral-500">
          <span className="tabular-nums text-amber-300">
            {summary.activeOverrides.toLocaleString()}
          </span>{' '}
          active ·{' '}
          <span className="tabular-nums text-neutral-300">
            {summary.revokedOverrides.toLocaleString()}
          </span>{' '}
          revoked
        </div>
      </ReportingInset>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="border-b border-neutral-800 text-neutral-500">
            <tr>
              <th className="px-3 py-2 font-medium">Check</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-right font-medium">Active</th>
              <th className="px-3 py-2 text-right font-medium">Latest</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800/50">
            {metrics.overrides.checks.map((check) => (
              <tr key={check.checkKey}>
                <td className="p-3 font-mono text-[11px] text-neutral-300">
                  {check.checkKey}
                </td>
                <td className="p-3 text-right tabular-nums text-neutral-300">
                  {check.totalOverrides.toLocaleString()}
                </td>
                <td className="p-3 text-right tabular-nums text-amber-300">
                  {check.activeOverrides.toLocaleString()}
                </td>
                <td className="p-3 text-right tabular-nums text-neutral-500">
                  {formatNullableDate(check.latestOverrideAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunRow({ run }: { run: ReportingAutomatedReviewRunLink }) {
  const displayPackage = run.dappPackageName ?? run.releasePackageName;

  return (
    <ReportingInset className="p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/tickets?ticketId=${run.ticketId}`}
              className="truncate text-sm font-medium text-teal-200 hover:text-teal-100"
            >
              Ticket {run.hubspotId}
            </Link>
            <ReportingStatusPill
              label={run.conclusion ?? run.status}
              tone={runTone(run)}
            />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
            {displayPackage && <span>{displayPackage}</span>}
            <span className="font-mono">{shortRunId(run.runId)}</span>
            <span>{run.policyVersion}</span>
          </div>
          {run.failedChecks.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {run.failedChecks.slice(0, 3).map((check) => (
                <ReportingStatusPill
                  key={`${run.runId}-${check}`}
                  label={check}
                  tone="amber"
                />
              ))}
            </div>
          )}
        </div>
        <div className="shrink-0 text-left sm:text-right">
          <div className="text-xs tabular-nums text-neutral-300">
            {formatNullableDate(run.createdAt)}
          </div>
          <div className="mt-0.5 text-[11px] text-neutral-500">
            {formatDuration(run.durationMs)}
          </div>
          <RunActions run={run} />
        </div>
      </div>
    </ReportingInset>
  );
}

function RunActions({ run }: { run: ReportingAutomatedReviewRunLink }) {
  const dappRoute =
    run.dappId !== null
      ? { id: run.dappId, packageName: run.dappPackageName }
      : null;

  return (
    <div className="mt-2 flex justify-start gap-2 sm:justify-end">
      {dappRoute && (
        <Link
          to={getAdminDappPath(dappRoute)}
          className="inline-flex items-center gap-1 rounded-lg border border-neutral-700/60 px-2 py-1 text-xs text-neutral-300 hover:border-neutral-600 hover:text-white"
        >
          <span>Dapp</span>
          <OpenInNewIcon className="size-3.5 fill-current" />
        </Link>
      )}
      {dappRoute && run.releaseId && (
        <Link
          to={getDappReleasePath(dappRoute, run.releaseId)}
          className="inline-flex items-center gap-1 rounded-lg border border-neutral-700/60 px-2 py-1 text-xs text-neutral-300 hover:border-neutral-600 hover:text-white"
        >
          <span>Release</span>
          <OpenInNewIcon className="size-3.5 fill-current" />
        </Link>
      )}
    </div>
  );
}

function RunLinkList({ runs }: { runs: ReportingAutomatedReviewRunLink[] }) {
  if (runs.length === 0) {
    return <span className="text-neutral-600">{'—'}</span>;
  }

  return (
    <div className="space-y-1">
      {runs.map((run) => {
        const displayPackage = run.dappPackageName ?? run.releasePackageName;
        return (
          <Link
            key={`${run.runId}-${run.ticketId}`}
            to={`/tickets?ticketId=${run.ticketId}`}
            className="block truncate text-teal-300 hover:text-teal-200"
            title={`${run.hubspotId} · ${displayPackage ?? 'unlinked'}`}
          >
            Ticket {run.hubspotId}
            {displayPackage ? ` · ${displayPackage}` : ''}
          </Link>
        );
      })}
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`size-2 rounded-full ${className}`} />
      {label}
    </span>
  );
}

function severityTone(
  severity: ReportingAutomatedReviewCheckMetric['severity'],
) {
  if (severity === 'Error') {
    return 'rose' as const;
  }

  if (severity === 'Warning') {
    return 'amber' as const;
  }

  return 'neutral' as const;
}

function runTone(run: ReportingAutomatedReviewRunLink) {
  if (run.conclusion === 'Failed' || run.conclusion === 'Error') {
    return 'rose' as const;
  }

  if (run.conclusion === 'Stale' || run.skippedCount > 0) {
    return 'amber' as const;
  }

  return 'neutral' as const;
}

function calloutToneClass(
  tone: ReportingAutomatedReviewMetrics['callouts'][number]['tone'],
) {
  switch (tone) {
    case 'teal':
      return 'bg-teal-300';
    case 'amber':
      return 'bg-amber-300';
    case 'rose':
      return 'bg-rose-300';
    case 'neutral':
      return 'bg-neutral-500';
  }
}

function formatPercent(value: number | null) {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function formatDuration(value: number | null) {
  if (value === null) {
    return '—';
  }

  if (value < 1000) {
    return `${Math.round(value).toLocaleString()} ms`;
  }

  const seconds = value / 1000;

  if (seconds < 60) {
    return `${seconds.toFixed(seconds >= 10 ? 0 : 1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  return `${minutes}m ${remainingSeconds}s`;
}

const shortDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

const shortDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatDate(value: number) {
  return shortDateFormatter.format(new Date(value));
}

function formatNullableDate(value: number | null) {
  if (value === null) {
    return '—';
  }

  return shortDateTimeFormatter.format(new Date(value));
}

function shortRunId(runId: string) {
  return runId.slice(0, 8);
}
